import { 
  HistoryEntry, 
  FoodItem, 
  BiomarkerLog, 
  LongevityMetrics, 
  GlycemicSpikeEvent, 
  CompensationProtocol, 
  CompensationStep,
  Language 
} from '../types';

// ==========================================
// 1. DIETARY AGES (kU) DATABASE & ESTIMATION
// ==========================================

interface AGEReferenceItem {
  keywords: string[];
  kU_per_100g: number;
}

const AGE_DATABASE: AGEReferenceItem[] = [
  // High AGEs (High-dry heat, charred, deep-fried meats & oils)
  { keywords: ['bacon', 'pork belly', 'fried pork', 'шашлик', 'сало смажене'], kU_per_100g: 11900 },
  { keywords: ['grilled steak', 'beef steak', 'ribeye', 'стейк', 'яловичина гриль'], kU_per_100g: 8200 },
  { keywords: ['fried chicken', 'chicken wings', 'nuggets', 'крильця', 'нагетси', 'смажена курка'], kU_per_100g: 7800 },
  { keywords: ['french fries', 'potato chips', 'картопля фрі', 'чипси'], kU_per_100g: 5200 },
  { keywords: ['burger', 'cheeseburger', 'бургер', 'гамбургер'], kU_per_100g: 6500 },
  { keywords: ['pizza', 'піца'], kU_per_100g: 4800 },
  { keywords: ['sausage', 'frankfurter', 'ковбаса', 'сосиски'], kU_per_100g: 6100 },
  { keywords: ['butter', 'margarine', 'mayonnaise', 'масло', 'майонез'], kU_per_100g: 4200 },
  { keywords: ['roasted nuts', 'смажені горіхи'], kU_per_100g: 2200 },

  // Moderate AGEs (Baked, pan-seared with moisture, cheeses)
  { keywords: ['baked fish', 'salmon', 'лосось', 'риба запечена'], kU_per_100g: 2100 },
  { keywords: ['roasted chicken breast', 'курка запечена'], kU_per_100g: 3400 },
  { keywords: ['hard cheese', 'cheddar', 'parmesan', 'сир твердий'], kU_per_100g: 2800 },
  { keywords: ['fried egg', 'яєчня'], kU_per_100g: 2700 },

  // Low / Inhibitory AGEs (Boiled, steamed, raw, plant-rich)
  { keywords: ['boiled egg', 'яйце варене'], kU_per_100g: 1200 },
  { keywords: ['boiled chicken', 'курка варена'], kU_per_100g: 1000 },
  { keywords: ['steamed fish', 'риба парова'], kU_per_100g: 800 },
  { keywords: ['tofu', 'тофу'], kU_per_100g: 600 },
  { keywords: ['cooked rice', 'рис'], kU_per_100g: 30 },
  { keywords: ['pasta', 'макарони', 'паста'], kU_per_100g: 45 },
  { keywords: ['oatmeal', 'вівсянка'], kU_per_100g: 25 },
  { keywords: ['apple', 'banana', 'orange', 'яблуко', 'банан', 'ягоди', 'berries'], kU_per_100g: 15 },
  { keywords: ['broccoli', 'spinach', 'salad', 'брокколі', 'шпинат', 'салат'], kU_per_100g: 20 },
  { keywords: ['olive oil', 'оливкова олія'], kU_per_100g: 800 },
  { keywords: ['kefir', 'yogurt', 'кефір', 'йогурт'], kU_per_100g: 150 },
];

export function estimateFoodAGEs(foodName: string, amountG: number, fatG: number, proteinG: number): number {
  const lower = (foodName || '').toLowerCase();
  const weight = Math.max(10, amountG || 100);

  for (const ref of AGE_DATABASE) {
    if (ref.keywords.some(k => lower.includes(k))) {
      return Math.round((ref.kU_per_100g * weight) / 100);
    }
  }

  // Heuristic based on macronutrients if food name is not matched
  // Heat + fat + protein drives non-enzymatic glycation
  const estimatedKuPer100g = Math.min(6000, Math.round(fatG * 80 + proteinG * 60 + 50));
  return Math.round((estimatedKuPer100g * weight) / 100);
}

export function calculateDailyAGEs(foods: FoodItem[]): { totalAgesKu: number; status: 'optimal' | 'moderate' | 'high_risk' } {
  let total = 0;
  for (const f of foods) {
    const amt = typeof f.amount === 'number' ? f.amount : (f.amount_g || 100);
    const itemAges = f.ages_ku ?? estimateFoodAGEs(f.name, amt, f.fat_g || 0, f.protein_g || 0);
    total += itemAges;
  }

  let status: 'optimal' | 'moderate' | 'high_risk' = 'optimal';
  if (total > 22000) {
    status = 'high_risk';
  } else if (total > 15000) {
    status = 'moderate';
  }

  return { totalAgesKu: total, status };
}

// ==========================================
// 2. DIETARY INFLAMMATORY INDEX (DII)
// ==========================================

export function calculateDietaryInflammatoryIndex(foods: FoodItem[]): { score: number; status: 'anti_inflammatory' | 'neutral' | 'pro_inflammatory'; proInflammatoryCount: number; antiInflammatoryCount: number } {
  let score = 0;
  let proCount = 0;
  let antiCount = 0;

  foods.forEach(f => {
    const lower = (f.name || '').toLowerCase();
    const fiber = f.fiber_g || 0;
    const sugar = f.sugar_g || 0;
    const fat = f.fat_g || 0;

    let foodScore = 0;

    // Positive anti-inflammatory drivers
    if (fiber >= 3) {
      foodScore -= 0.5 * (fiber / 3);
      antiCount++;
    }
    if (lower.includes('salmon') || lower.includes('лосось') || lower.includes('omega') || lower.includes('tuna') || lower.includes('тунець')) {
      foodScore -= 1.2; // High Omega-3 EPA/DHA
      antiCount++;
    }
    if (lower.includes('berry') || lower.includes('ягоди') || lower.includes('spinach') || lower.includes('шпинат') || lower.includes('broccoli') || lower.includes('брокколі') || lower.includes('matcha') || lower.includes('матча')) {
      foodScore -= 0.8; // High polyphenol & sulforaphane
      antiCount++;
    }
    if (lower.includes('olive oil') || lower.includes('оливкова олія') || lower.includes('avocado') || lower.includes('авокадо')) {
      foodScore -= 0.6;
      antiCount++;
    }

    // Negative pro-inflammatory drivers
    if (sugar >= 12) {
      foodScore += 0.6 * (sugar / 12);
      proCount++;
    }
    if (lower.includes('fry') || lower.includes('смажен') || lower.includes('chips') || lower.includes('чипс') || lower.includes('donut') || lower.includes('пончик') || lower.includes('nugget')) {
      foodScore += 1.4; // Oxidized seed oils & trans isomers
      proCount++;
    }
    if (lower.includes('sausage') || lower.includes('сосис') || lower.includes('bacon') || lower.includes('бекон') || lower.includes('salami')) {
      foodScore += 1.1; // Advanced nitrosamines & processed red meat
      proCount++;
    }
    if (lower.includes('soda') || lower.includes('cola') || lower.includes('кола') || lower.includes('енергетик') || lower.includes('energy drink')) {
      foodScore += 1.3; // High fructose / artificial additives
      proCount++;
    }

    score += foodScore;
  });

  // Clamp DII between -5.0 and +5.0
  const normalizedScore = Math.max(-5.0, Math.min(5.0, Math.round(score * 10) / 10));

  let status: 'anti_inflammatory' | 'neutral' | 'pro_inflammatory' = 'neutral';
  if (normalizedScore <= -0.5) status = 'anti_inflammatory';
  else if (normalizedScore >= 0.8) status = 'pro_inflammatory';

  return {
    score: normalizedScore,
    status,
    proInflammatoryCount: proCount,
    antiInflammatoryCount: antiCount
  };
}

// ==========================================
// 3. GLYCEMIC LOAD & SPIKE SENTINEL
// ==========================================

const HIGH_GI_KEYWORDS = ['white bread', 'білий хліб', 'sugar', 'цукор', 'soda', 'кола', 'donut', 'пончик', 'cake', 'торт', 'candy', 'цукерки', 'syrup', 'сироп', 'fries', 'фрі', 'pizza', 'піца', 'croissant', 'круасан'];

export function detectGlycemicSpikes(meal: HistoryEntry | { meal_name: string; foods: FoodItem[]; timestamp?: number }): GlycemicSpikeEvent | null {
  const foods = meal.foods || [];
  let totalCarbs = 0;
  let totalFiber = 0;
  let totalFat = 0;
  let totalProtein = 0;
  const triggerFoods: string[] = [];

  foods.forEach(f => {
    totalCarbs += f.carbs_g || 0;
    totalFiber += f.fiber_g || 0;
    totalFat += f.fat_g || 0;
    totalProtein += f.protein_g || 0;

    const lower = (f.name || '').toLowerCase();
    if (HIGH_GI_KEYWORDS.some(k => lower.includes(k)) || (f.sugar_g && f.sugar_g > 15)) {
      triggerFoods.push(f.name);
    }
  });

  // Effective net glycemic load calculation
  const netCarbs = Math.max(0, totalCarbs - (totalFiber * 1.5));
  // Buffering factor: fat + protein delays gastric emptying
  const bufferingFactor = Math.max(0.65, 1 - (totalProtein * 0.005 + totalFat * 0.004));
  const estimatedGL = Math.round((netCarbs * 0.7) * bufferingFactor);

  // Spike threshold: GL >= 20 or trigger food with high sugar and low fiber
  const isSpike = estimatedGL >= 20 || (triggerFoods.length > 0 && totalFiber < 3 && netCarbs > 25);

  if (!isSpike) return null;

  let severity: 'mild' | 'moderate' | 'severe' = 'mild';
  if (estimatedGL > 32 || (triggerFoods.length >= 2 && netCarbs > 60)) {
    severity = 'severe';
  } else if (estimatedGL >= 24) {
    severity = 'moderate';
  }

  const estimatedSpikeMgDl = Math.round(35 + (estimatedGL * 1.8));

  return {
    mealId: (meal as any).id || `spike-${Date.now()}`,
    mealName: (meal as any).meal_name || 'Logged Meal',
    timestamp: meal.timestamp || Date.now(),
    estimatedSpikeMgDl,
    glycemicLoad: estimatedGL,
    severity,
    triggerFoods: triggerFoods.length > 0 ? triggerFoods : [foods[0]?.name || 'Carb-dense food']
  };
}

// ==========================================
// 4. ACTIONABLE COMPENSATION PROGRAM
// ==========================================

export function generateCompensationProgram(
  spike: GlycemicSpikeEvent,
  lang: Language = 'en-US'
): CompensationProtocol {
  const isUa = lang.startsWith('ua');

  const steps: CompensationStep[] = [
    {
      id: 'step-movement',
      title: {
        en: '12-15 Min GLUT-4 Zone-2 Postprandial Walk',
        ua: '12-15 хв Прогулянка (Активація GLUT-4)'
      },
      description: {
        en: 'Brisk walking or soleus calf raises directly stimulate skeletal muscle glucose transporters (GLUT-4) independent of insulin, shaving peak post-meal glucose by up to 34%.',
        ua: 'Швидка ходьба або підйоми на носки активують транспортери глюкози GLUT-4 у м’язах без участі інсуліну, знижуючи пік цукру на 34%.'
      },
      timing: isUa ? 'Протягом 0-30 хв після їжі' : '0-30 min post-meal',
      category: 'movement',
      completed: false,
      scientificRationale: {
        en: 'Muscle contraction-mediated glucose uptake circumvents insulin resistance and rapidly clears blood sugar spikes.',
        ua: 'М’язове скорочення очищує глюкозу з кровотоку напряму, захищаючи ендотелій від глікаційного стресу.'
      }
    },
    {
      id: 'step-acid-buffer',
      title: {
        en: 'Acetic Acid / Lemon & Water Buffer (300ml)',
        ua: 'Оцтовий або Лимонний Буфер (300 мл)'
      },
      description: {
        en: 'Drink 300ml cold water with 1 tbsp raw apple cider vinegar or freshly squeezed lemon juice. Acetic acid inhibits alpha-amylase activity in the small intestine.',
        ua: 'Випийте 300 мл води з 1 ст. л. яблучного оцту або лимонного соку. Оцтова кислота гальмує фермент альфа-амілазу, сповільнюючи розпад вуглеводів.'
      },
      timing: isUa ? 'Негайно або до 45 хв' : 'Immediately or within 45 min',
      category: 'biochemical_buffer',
      completed: false,
      scientificRationale: {
        en: 'Acetic acid reduces postprandial hyperglycemia and enhances liver glycogen synthesis.',
        ua: 'Оцтова кислота зменшує постпрандіальну гіперглікемію та прискорює синтез глікогену в печінці.'
      }
    },
    {
      id: 'step-antioxidant-shield',
      title: {
        en: 'Polyphenol & Mucosa Antioxidant Shield',
        ua: 'Поліфенольний Щит проти Окислення'
      },
      description: {
        en: 'Consume 1 cup of unsweetened green tea / ceremonial matcha or 50g of fresh blueberries to neutralize reactive oxygen species (ROS) released by AGEs and glycation.',
        ua: 'Випийте чашку зеленого чаю (матча) або з’їжте 50 г чорниці/ягід, щоб нейтралізувати вільні радикали, викликані стрибком цукру та AGEs.'
      },
      timing: isUa ? 'Протягом 1-2 годин' : 'Within 1-2 hours',
      category: 'microbiome',
      completed: false,
      scientificRationale: {
        en: 'Epigallocatechin gallate (EGCG) and anthocyanins quench vascular oxidative burst and protect collagen matrices.',
        ua: 'EGCG та антоціани гасять окислювальний стрес у судинах і захищають колагеновий матрикс від старіння.'
      }
    },
    {
      id: 'step-evening-reset',
      title: {
        en: 'Evening Counterbalance Reset',
        ua: 'Вечірня Компенсаційна Стабілізація'
      },
      description: {
        en: 'Design your next meal around leafy greens, wild salmon or tofu, and fermented fiber to restore microbiome diversity and deplete excess hepatic glycogen.',
        ua: 'Побудуйте наступний прийом їжі навколо листової зелені, риби або тофу та ферментованої клітковини для відновлення балансу.'
      },
      timing: isUa ? 'Наступний прийом їжі' : 'Next meal',
      category: 'meal_reset',
      completed: false,
      scientificRationale: {
        en: 'High potassium and insoluble fiber restore cellular electrolyte gradients and soothe gut lining after inflammatory load.',
        ua: 'Високий вміст калію та клітковини нормалізує електролітний градієнт та заспокоює мікробіоту.'
      }
    }
  ];

  return {
    id: `protocol-${spike.mealId}`,
    triggerMealName: spike.mealName,
    timestamp: spike.timestamp,
    severity: spike.severity === 'severe' ? 'critical' : spike.severity === 'moderate' ? 'high' : 'moderate',
    steps,
    recommendedCounterMeal: {
      name: {
        en: 'Wild Steamed Salmon with Asparagus & Sauerkraut',
        ua: 'Дикий Лосось на пару з Спаржею та Квашеною Капустою'
      },
      description: {
        en: 'Rich in EPA/DHA omega-3, sulforaphane, and bioactive lactic acid probiotics. Zero glycemic impact.',
        ua: 'Багатий на Омега-3, сульфорафан та живі пробіотики. Нульове глікемічне навантаження.'
      },
      keyIngredients: ['Wild salmon', 'Steamed asparagus', 'Raw fermented sauerkraut', 'Extra virgin olive oil', 'Lemon wedge']
    }
  };
}

// ==========================================
// 5. LONGEVITY ROI & BIOLOGICAL AGE DELTA
// ==========================================

export function calculateHealthROI(
  history: HistoryEntry[],
  biomarkers: BiomarkerLog[],
  todayFoods: FoodItem[],
  stepsToday: number = 0,
  sleepHours: number = 7
): LongevityMetrics {
  const { totalAgesKu, status: agesStatus } = calculateDailyAGEs(todayFoods);
  const { score: diiScore, status: diiStatus } = calculateDietaryInflammatoryIndex(todayFoods);

  // Total fiber today
  const fiberG = todayFoods.reduce((acc, f) => acc + (f.fiber_g || 0), 0);
  const sugarG = todayFoods.reduce((acc, f) => acc + (f.sugar_g || 0), 0);

  // Longevity Dividend in minutes earned/lost today:
  // Base daily dividend: +40 mins for general healthy living
  let dailyDividendMinutes = 40;

  // Fiber bonus: +4 mins per gram above 15g, up to +45 mins
  if (fiberG >= 25) dailyDividendMinutes += 45;
  else if (fiberG >= 15) dailyDividendMinutes += (fiberG - 15) * 4;

  // Physical activity (steps):
  if (stepsToday >= 10000) dailyDividendMinutes += 50;
  else if (stepsToday >= 7000) dailyDividendMinutes += 30;
  else if (stepsToday < 3500) dailyDividendMinutes -= 25;

  // Sleep architecture:
  if (sleepHours >= 7.5 && sleepHours <= 9) dailyDividendMinutes += 40;
  else if (sleepHours < 6) dailyDividendMinutes -= 45;

  // AGEs penalty/bonus:
  if (totalAgesKu < 12000) dailyDividendMinutes += 35;
  else if (totalAgesKu > 22000) dailyDividendMinutes -= 40;

  // DII penalty/bonus:
  if (diiScore <= -1.0) dailyDividendMinutes += 40;
  else if (diiScore >= 1.5) dailyDividendMinutes -= 45;

  // Excess sugar penalty:
  if (sugarG > 50) dailyDividendMinutes -= Math.round((sugarG - 50) * 0.8);

  // Biological Age Delta:
  let bioAgeDelta = -1.2; // Baseline -1.2 years younger
  if (dailyDividendMinutes > 120) bioAgeDelta = -2.8;
  else if (dailyDividendMinutes > 80) bioAgeDelta = -2.2;
  else if (dailyDividendMinutes > 30) bioAgeDelta = -1.5;
  else if (dailyDividendMinutes < 0) bioAgeDelta = +0.8;

  // Recent HRV modifier if available
  const recentHrv = biomarkers[0]?.hrv_rmssd;
  if (recentHrv && recentHrv > 65) bioAgeDelta -= 0.4;
  else if (recentHrv && recentHrv < 30) bioAgeDelta += 0.5;

  // Health ROI Score (0 to 100)
  const healthRoiScore = Math.max(10, Math.min(99, Math.round(50 + (dailyDividendMinutes / 4))));

  // Cumulative dividend hours (historical 30-day projection)
  const cumulativeHours = Math.round((dailyDividendMinutes * 24) / 60);

  const metabolicFlexibility = Math.max(20, Math.min(98, Math.round(
    60 + (fiberG * 0.8) - (sugarG * 0.4) + (stepsToday > 8000 ? 15 : 0) + (diiScore < 0 ? 10 : -10)
  )));

  return {
    health_roi_score: healthRoiScore,
    biological_age_delta: Math.round(bioAgeDelta * 10) / 10,
    longevity_dividend_minutes: dailyDividendMinutes,
    cumulative_dividend_hours: cumulativeHours,
    daily_ages_ku: totalAgesKu,
    ages_threshold_status: agesStatus,
    dietary_inflammatory_index: diiScore,
    inflammation_status: diiStatus,
    metabolic_flexibility_score: metabolicFlexibility
  };
}

// ==========================================
// 6. HEALTH ROI GRAPH TRAJECTORY BUILDER
// ==========================================

export interface HealthRoiPoint {
  day: string;
  baseline: number; // Chronological normal decay line
  healthRoi: number; // User trajectory with longevity dividends
  projectedAge: number; // Projected biological age
  agesKu: number;
}

export function generateHealthRoiTrajectory(
  metrics: LongevityMetrics,
  lang: Language = 'en-US'
): HealthRoiPoint[] {
  const isUa = lang.startsWith('ua');
  const daysEn = ['Day 1', 'Day 5', 'Day 10', 'Day 15', 'Day 20', 'Day 25', 'Day 30', '+60d Proj', '+90d Proj'];
  const daysUa = ['День 1', 'День 5', 'День 10', 'День 15', 'День 20', 'День 25', 'День 30', '+60д Прогноз', '+90д Прогноз'];
  const labels = isUa ? daysUa : daysEn;

  const baseRoi = metrics.health_roi_score;
  const growthRate = metrics.longevity_dividend_minutes > 0 ? 1.8 : -1.2;

  return labels.map((label, idx) => {
    const projectionFactor = idx * growthRate;
    const currentScore = Math.max(25, Math.min(98, Math.round(baseRoi + projectionFactor + (Math.sin(idx) * 2))));
    const baselineDecay = Math.round(50 - (idx * 0.5));
    const bioAge = Math.max(22, Math.round((35 + metrics.biological_age_delta - (idx * 0.08)) * 10) / 10);
    const estimatedAges = Math.max(8000, Math.round(metrics.daily_ages_ku + (Math.sin(idx * 1.5) * 2500)));

    return {
      day: label,
      baseline: baselineDecay,
      healthRoi: currentScore,
      projectedAge: bioAge,
      agesKu: estimatedAges
    };
  });
}

// ==========================================
// 7. DEFAULT BIOMARKERS DATASET (DEMO / REAL)
// ==========================================

export const DEFAULT_BIOMARKER_LOGS: BiomarkerLog[] = [
  {
    id: 'bio-1',
    timestamp: Date.now() - 3600 * 1000 * 2,
    hrv_rmssd: 68,
    fasting_glucose_mgdl: 86,
    postprandial_glucose_mgdl: 112,
    resting_heart_rate: 54,
    blood_oxygen_pct: 99,
    blood_pressure_systolic: 118,
    blood_pressure_diastolic: 76,
    hba1c_pct: 5.1,
    hs_crp_mgl: 0.6,
    apob_mgdl: 78,
    vitamin_d_ngml: 52,
    deep_sleep_minutes: 105,
    rem_sleep_minutes: 98,
    source: 'wearable',
    note: 'Optimal autonomic tone & deep sleep recovery'
  },
  {
    id: 'bio-2',
    timestamp: Date.now() - 86400 * 1000 * 1,
    hrv_rmssd: 64,
    fasting_glucose_mgdl: 89,
    postprandial_glucose_mgdl: 124,
    resting_heart_rate: 56,
    deep_sleep_minutes: 95,
    source: 'wearable'
  },
  {
    id: 'bio-3',
    timestamp: Date.now() - 86400 * 1000 * 2,
    hrv_rmssd: 58,
    fasting_glucose_mgdl: 92,
    postprandial_glucose_mgdl: 138,
    resting_heart_rate: 58,
    deep_sleep_minutes: 80,
    source: 'wearable'
  }
];
